import React, { useState } from 'react';
import ScrollAnimation from './ScrollAnimation';

// Spec icons from ProductShowcase
const SpecIcons = {
  android: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67c-.19-.28-.54-.37-.83-.22-.3.16-.42.54-.26.85l1.84 3.18C4.5 11.1 3 13.9 3 17c0 2.5 2 4.5 4.5 4.5h13c2.5 0 4.5-2 4.5-4.5 0-3.1-1.5-5.9-3.4-7.52zM6.75 14c-.69 0-1.25-.56-1.25-1.25S6.06 11.5 6.75 11.5 8 12.06 8 12.75 7.44 14 6.75 14zm10.5 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
    </svg>
  ),
  chip: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
    </svg>
  ),
  display: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  batteryRect: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="2" y="7" width="16" height="10" rx="2" ry="2" strokeWidth={2} />
      <line x1="22" y1="11" x2="22" y2="13" strokeWidth={2} strokeLinecap="round" />
    </svg>
  ),
};

// Specs from ProductShowcase - mapped to feature items (marketing titles + technical descriptions)
const PERFORMANCE_FEATURES = [
  {
    id: 1,
    icon: SpecIcons.android,
    title: 'نظام تشغيل حديث',
    description: 'Android 13',
    align: 'left'
  },
  {
    id: 2,
    icon: SpecIcons.chip,
    title: 'أداء فائق السرعة',
    description: '3GB RAM + 32GB ROM',
    align: 'left'
  },
  {
    id: 3,
    icon: SpecIcons.display,
    title: 'شاشة عالية الدقة',
    description: '5.5" HD+ • 1440×720',
    align: 'right'
  },
  {
    id: 4,
    icon: SpecIcons.batteryRect,
    title: 'بطارية قوية',
    description: '7.7V 3000mAh • قابلة للإزالة',
    align: 'right'
  }
];

const FeatureItem = ({ icon: Icon, title, description, align = 'left', delay, isHovered, onHover, onLeave }) => {
  const flexDirection = align === 'right' ? 'md:flex-row-reverse md:text-right' : align === 'center' ? '' : 'md:flex-row md:text-left';
  const textAlign = align === 'center' ? 'text-center' : 'text-center md:text-right';
  const iconOrder = align === 'center' ? '' : 'order-1 md:order-2';
  const textOrder = align === 'center' ? 'flex-1' : 'md:flex-1 order-2 md:order-1';
  const textItemsAlign = align === 'right' ? 'md:items-end' : align === 'center' ? 'items-center' : 'md:items-start';
  const maxWidth = align === 'center' ? 'max-w-none' : 'max-w-[140px] md:max-w-none';
  
  return (
    <div 
      className={`flex flex-col items-center gap-2 md:gap-6 ${flexDirection} ${textAlign} w-full relative group cursor-default p-2`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      {/* Icon Container */}
      <div className={`relative flex-shrink-0 z-20 ${iconOrder}`}>
        <div className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl shadow-lg flex items-center justify-center relative transition-all duration-500 ${isHovered ? 'bg-primary-500 text-white scale-110 shadow-primary-200 shadow-xl' : 'bg-white text-primary-500 border border-gray-100'}`}>
          {Icon && <Icon className="w-5 h-5 md:w-8 md:h-8 flex-shrink-0" />}
        </div>
        
        {/* Pulse Effect */}
        <div className={`absolute inset-0 rounded-2xl bg-primary-500/20 -z-10 transition-all duration-500 ${isHovered ? 'scale-150 opacity-100' : 'scale-100 opacity-0'}`}></div>
      </div>

      {/* Text Content */}
      <div className={`flex flex-col ${textOrder} ${textItemsAlign} items-center transition-all duration-300 ${isHovered ? 'transform md:translate-x-2' : ''}`}>
        <h3 className={`text-xs md:text-xl font-bold text-gray-900 leading-tight transition-colors duration-300 ${isHovered ? 'text-primary-500' : ''}`}>{title}</h3>
        <p className={`text-xs md:text-sm text-gray-500 mt-1 leading-relaxed ${maxWidth}`}>{description}</p>
      </div>
    </div>
  );
};

export default function PerformanceFeatures() {
  const [hoveredFeature, setHoveredFeature] = useState(null);

  // Calculate device transform based on hovered feature - device moves toward hovered item
  const getDeviceTransform = () => {
    switch(hoveredFeature) {
      case 1: // Right Top (Android) - move device right and up
        return 'translate(12px, -12px) rotate(-3deg)';
      case 2: // Right Bottom (RAM) - move device right and down
        return 'translate(12px, 12px) rotate(3deg)';
      case 3: // Left Top (Display) - move device left and up
        return 'translate(-12px, -12px) rotate(3deg)';
      case 4: // Left Bottom (Battery) - move device left and down
        return 'translate(-12px, 12px) rotate(-3deg)';
      default:
        return 'translate(0, 0) rotate(0deg)';
    }
  };


  return (
    <section className="relative w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 md:py-32 overflow-hidden font-sans">
      
      {/* Background Decorative Blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-100/30 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-50/30 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10 text-right" dir="rtl">
        {/* Header */}
        <div className="text-center mb-8 md:mb-32 animate-fade-in-up">
          <div className="inline-block px-4 py-1.5 mb-4 rounded-full bg-primary-50 border border-primary-100 text-primary-600 text-xs md:text-sm font-medium">
            أداء لا يضاهى
          </div>
          <h2 className="text-2xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            قوة <span className="text-primary-500 relative inline-block">
              الأداء
              <svg className="absolute w-full h-2 md:h-3 -bottom-1 right-0 text-primary-200 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
              </svg>
            </span> في راحة يدك
          </h2>
        </div>

        {/* Mobile: Features around device (like desktop) */}
        <div className="md:hidden relative max-w-md mx-auto">
          <div className="grid grid-cols-3 gap-2 items-center">
            {/* Left Column - Display and Battery */}
            <div className="col-span-1 flex flex-col gap-6 justify-center">
              <ScrollAnimation variant="fade-right" delay={200}>
                <FeatureItem 
                  align="right"
                  icon={PERFORMANCE_FEATURES[2].icon}
                  title={PERFORMANCE_FEATURES[2].title}
                  description={PERFORMANCE_FEATURES[2].description}
                  isHovered={hoveredFeature === 3}
                  onHover={() => setHoveredFeature(3)}
                  onLeave={() => setHoveredFeature(null)}
                />
              </ScrollAnimation>

              <ScrollAnimation variant="fade-right" delay={400}>
                <FeatureItem 
                  align="right"
                  icon={PERFORMANCE_FEATURES[3].icon}
                  title={PERFORMANCE_FEATURES[3].title}
                  description={PERFORMANCE_FEATURES[3].description}
                  isHovered={hoveredFeature === 4}
                  onHover={() => setHoveredFeature(4)}
                  onLeave={() => setHoveredFeature(null)}
                />
              </ScrollAnimation>
            </div>

            {/* Center Device */}
            <div className="col-span-1 flex justify-center z-10 relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150px] h-[150px] bg-gradient-to-br from-primary-500/10 to-primary-500/5 rounded-full blur-2xl -z-10"></div>
              <ScrollAnimation variant="zoom-in" delay={0}>
                <img 
                  src="/device.png" 
                  alt="جهاز نقاط البيع Casher V5" 
                  className="max-h-[200px] w-auto object-contain drop-shadow-2xl transition-all duration-500"
                  style={{
                    transform: hoveredFeature ? `scale(105%) ${getDeviceTransform()}` : 'scale(100%) translate(0, 0) rotate(0deg)'
                  }}
                />
              </ScrollAnimation>
            </div>

            {/* Right Column - Android and RAM */}
            <div className="col-span-1 flex flex-col gap-6 justify-center">
              <ScrollAnimation variant="fade-left" delay={100}>
                <FeatureItem 
                  align="left"
                  icon={PERFORMANCE_FEATURES[0].icon}
                  title={PERFORMANCE_FEATURES[0].title}
                  description={PERFORMANCE_FEATURES[0].description}
                  isHovered={hoveredFeature === 1}
                  onHover={() => setHoveredFeature(1)}
                  onLeave={() => setHoveredFeature(null)}
                />
              </ScrollAnimation>

              <ScrollAnimation variant="fade-left" delay={300}>
                <FeatureItem 
                  align="left"
                  icon={PERFORMANCE_FEATURES[1].icon}
                  title={PERFORMANCE_FEATURES[1].title}
                  description={PERFORMANCE_FEATURES[1].description}
                  isHovered={hoveredFeature === 2}
                  onHover={() => setHoveredFeature(2)}
                  onLeave={() => setHoveredFeature(null)}
                />
              </ScrollAnimation>
            </div>
          </div>
        </div>

        {/* Desktop: Original Layout */}
        <div className="hidden md:grid relative grid-cols-3 gap-y-12 gap-x-4 items-center max-w-6xl mx-auto">
          
          {/* Right Column - Android and RAM */}
          <div className="col-span-1 flex flex-col gap-24 pl-0 justify-start">
             <div className="relative flex-none flex items-center justify-end">
               <ScrollAnimation variant="fade-left" delay={100}>
                  <FeatureItem 
                    align="left"
                    icon={PERFORMANCE_FEATURES[0].icon}
                    title={PERFORMANCE_FEATURES[0].title}
                    description={PERFORMANCE_FEATURES[0].description}
                    isHovered={hoveredFeature === 1}
                    onHover={() => setHoveredFeature(1)}
                    onLeave={() => setHoveredFeature(null)}
                  />
               </ScrollAnimation>
            </div>

            <div className="relative flex-none flex items-center justify-end">
               <ScrollAnimation variant="fade-left" delay={300}>
                <FeatureItem 
                  align="left"
                  icon={PERFORMANCE_FEATURES[1].icon}
                  title={PERFORMANCE_FEATURES[1].title}
                  description={PERFORMANCE_FEATURES[1].description}
                  isHovered={hoveredFeature === 2}
                  onHover={() => setHoveredFeature(2)}
                  onLeave={() => setHoveredFeature(null)}
                />
               </ScrollAnimation>
            </div>
          </div>

          {/* Center Image */}
          <div className="col-span-1 flex justify-center z-10 relative h-auto">
            {/* Center Hub Colors */}
            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] h-[220px] md:w-[450px] md:h-[450px] bg-gradient-to-br from-primary-500/10 to-primary-500/5 rounded-full blur-2xl -z-10 transition-all duration-700 ${hoveredFeature ? 'scale-110 opacity-80' : 'scale-100 opacity-50'}`}></div>

            <div className="relative w-full h-full flex items-center justify-center z-10">
               <ScrollAnimation variant="zoom-in" delay={0}>
                 <img 
                  src="/device.png" 
                  alt="جهاز نقاط البيع Casher V5" 
                  className="max-h-[300px] md:max-h-[500px] w-auto object-contain drop-shadow-2xl transition-all duration-500 ease-out"
                  style={{
                    transform: hoveredFeature ? `scale(105%) ${getDeviceTransform()}` : 'scale(100%) translate(0, 0) rotate(0deg)'
                  }}
                />
               </ScrollAnimation>
            </div>
          </div>

          {/* Left Column - Display and Battery */}
          <div className="col-span-1 flex flex-col gap-24 pr-0 justify-start">
             <div className="relative flex-none flex items-center justify-start">
              <ScrollAnimation variant="fade-right" delay={200}>
                <FeatureItem 
                  align="right"
                  icon={PERFORMANCE_FEATURES[2].icon}
                  title={PERFORMANCE_FEATURES[2].title}
                  description={PERFORMANCE_FEATURES[2].description}
                  isHovered={hoveredFeature === 3}
                  onHover={() => setHoveredFeature(3)}
                  onLeave={() => setHoveredFeature(null)}
                />
              </ScrollAnimation>
            </div>

            <div className="relative flex-none flex items-center justify-start">
              <ScrollAnimation variant="fade-right" delay={400}>
                <FeatureItem 
                  align="right"
                  icon={PERFORMANCE_FEATURES[3].icon}
                  title={PERFORMANCE_FEATURES[3].title}
                  description={PERFORMANCE_FEATURES[3].description}
                  isHovered={hoveredFeature === 4}
                  onHover={() => setHoveredFeature(4)}
                  onLeave={() => setHoveredFeature(null)}
                />
              </ScrollAnimation>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
